import React, { useEffect, useState } from 'react';
import { UsersIcon, SearchIcon, FilterIcon, PlusIcon, EditIcon, TrashIcon, PhoneIcon, MailIcon, BuildingIcon, MapPinIcon, BriefcaseIcon, AlertTriangleIcon, XIcon, CheckIcon, EyeIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { ContactForm, ContactData } from './ContactForm';
// Mock data for centralized contacts
const initialContacts: ContactData[] = [
// Org Unit Contacts
{
  id: 'con-001',
  name: 'Sarah Chen',
  role: 'Site Manager',
  isPrimary: true,
  emails: [{
    id: 'e1',
    type: 'Work',
    value: 'sarah.chen@apactech.com.au',
    isPrimary: true
  }],
  phones: [{
    id: 'p1',
    type: 'Mobile',
    value: '+61 400 123 456',
    isPrimary: true
  }, {
    id: 'p2',
    type: 'Work',
    value: '+61 2 9876 5432',
    isPrimary: false
  }],
  addresses: [{
    id: 'a1',
    type: 'Physical',
    street: '123 Tech Park Rd',
    city: 'Sydney',
    state: 'NSW',
    postalCode: '2000',
    country: 'Australia',
    isPrimary: true
  }],
  association: {
    type: 'Org Unit',
    name: 'Sydney Office',
    id: 'org-007'
  }
}, {
  id: 'con-002',
  name: 'Michael Wong',
  role: 'Managing Director',
  isPrimary: true,
  emails: [{
    id: 'e1',
    type: 'Work',
    value: 'michael.wong@apactech.com.au',
    isPrimary: true
  }],
  phones: [{
    id: 'p1',
    type: 'Work',
    value: '+61 2 9876 5400',
    isPrimary: true
  }],
  addresses: [],
  association: {
    type: 'Org Unit',
    name: 'Asia Pacific Technologies Pty Ltd',
    id: 'org-006'
  }
}, {
  id: 'con-003',
  name: 'Jane Doe',
  role: 'CEO',
  isPrimary: true,
  emails: [{
    id: 'e1',
    type: 'Work',
    value: 'jane.doe@globaltech.com',
    isPrimary: true
  }],
  phones: [{
    id: 'p1',
    type: 'Work',
    value: '+1 (555) 987-6543',
    isPrimary: true
  }],
  addresses: [],
  association: {
    type: 'Org Unit',
    name: 'Global Technologies Inc.',
    id: 'org-001'
  }
}, {
  id: 'con-005',
  name: 'David Miller',
  role: 'Account Manager',
  isPrimary: true,
  emails: [{
    id: 'e1',
    type: 'Work',
    value: 'd.miller@officedepot.com',
    isPrimary: true
  }],
  phones: [{
    id: 'p1',
    type: 'Work',
    value: '+1 (555) 222-3333',
    isPrimary: true
  }],
  addresses: [{
    id: 'a1',
    type: 'Billing',
    street: '6600 North Military Trail',
    city: 'Boca Raton',
    state: 'FL',
    postalCode: '33496',
    country: 'USA',
    isPrimary: true
  }],
  association: {
    type: 'Vendor',
    name: 'Office Depot',
    id: 'vendor-001'
  }
}, {
  id: 'con-006',
  name: 'Jennifer Wu',
  role: 'Sales Representative',
  isPrimary: true,
  emails: [{
    id: 'e1',
    type: 'Work',
    value: 'j.wu@dell.com',
    isPrimary: true
  }],
  phones: [{
    id: 'p1',
    type: 'Work',
    value: '+1 (555) 444-5555',
    isPrimary: true
  }],
  addresses: [],
  association: {
    type: 'Vendor',
    name: 'Dell Technologies',
    id: 'vendor-002'
  }
}];
export function ContactsManagement({
  locale = 'en-US'
}) {
  const [contacts, setContacts] = useState<any[]>(initialContacts);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState<any>(null);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  // Filter contacts based on search and type
  const filteredContacts = contacts.filter(contact => {
    const primaryEmail = contact.emails?.find((e: any) => e.isPrimary)?.value || contact.email || '';
    const matchesSearch = contact.name.toLowerCase().includes(searchTerm.toLowerCase()) || primaryEmail.toLowerCase().includes(searchTerm.toLowerCase()) || contact.role.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'All' || contact.association && contact.association.type === filterType;
    return matchesSearch && matchesType;
  });
  // Pagination calculations
  const totalItems = filteredContacts.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedContacts = filteredContacts.slice(startIndex, endIndex);
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType]);
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  const handleViewContact = (contact: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedContact(contact);
  };
  const handleAddContact = () => {
    setEditingContact(null);
    setShowContactForm(true);
  };
  const handleEditContact = (contact: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingContact(contact);
    setShowContactForm(true);
  };
  const handleDeleteClick = (contact: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(contact);
  };
  const confirmDelete = () => {
    if (showDeleteConfirm) {
      setContacts(contacts.filter(c => c.id !== showDeleteConfirm.id));
      setShowDeleteConfirm(null);
      if (selectedContact?.id === showDeleteConfirm.id) {
        setSelectedContact(null);
      }
    }
  };
  const handleSaveContact = (formData: any) => {
    if (formData.id) {
      // Update existing
      setContacts(contacts.map(c => c.id === formData.id ? {
        ...c,
        ...formData,
        association: c.association || null
      } : c));
      // Update selected contact if it's the one being edited
      if (selectedContact?.id === formData.id) {
        setSelectedContact({
          ...selectedContact,
          ...formData,
          association: selectedContact.association || null
        });
      }
    } else {
      // Add new
      const newContact = {
        ...formData,
        id: `con-${Date.now()}`,
        association: null // New contacts have no association initially
      };
      setContacts([...contacts, newContact]);
    }
    setShowContactForm(false);
  };
  const getAssociationIcon = (type: string) => {
    switch (type) {
      case 'Vendor':
        return <BriefcaseIcon className="h-3 w-3 mr-1" data-id="element-9382" />;
      case 'Org Unit':
        return <UsersIcon className="h-3 w-3 mr-1" data-id="element-9383" />;
      default:
        return <UsersIcon className="h-3 w-3 mr-1" data-id="element-9384" />;
    }
  };
  const getAssociationColor = (type: string) => {
    switch (type) {
      case 'Vendor':
        return 'bg-blue-100 text-blue-800';
      case 'Org Unit':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Helper to get primary contact info safely
  const getPrimaryEmail = (contact: any) => {
    if (contact.emails && contact.emails.length > 0) {
      return contact.emails.find((e: any) => e.isPrimary)?.value || contact.emails[0].value;
    }
    return contact.email || '';
  };
  const getPrimaryPhone = (contact: any) => {
    if (contact.phones && contact.phones.length > 0) {
      return contact.phones.find((p: any) => p.isPrimary)?.value || contact.phones[0].value;
    }
    return contact.phone || '';
  };
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-9385">
      <div className="sm:flex sm:items-center" data-id="element-9386">
        <div className="sm:flex-auto" data-id="element-9387">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-9388">Contacts</h1>
          <p className="mt-2 text-sm text-gray-700" data-id="element-9389">
            A centralized list of all contacts across the organization,
            including their associations with vendors, entities, sites, and
            organizational units.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-9390">
        <div className="relative max-w-xs w-full" data-id="element-9391">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3" data-id="element-9392">
            <SearchIcon className="h-5 w-5 text-gray-400" aria-hidden="true" data-id="element-9393" />
          </div>
          <input type="text" className="block w-full rounded-md border-gray-300 pl-10 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="Search contacts..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-9394" />
        </div>

        <div className="flex items-center gap-2" data-id="element-9395">
          <FilterIcon className="h-5 w-5 text-gray-400" data-id="element-9396" />
          <span className="text-sm text-gray-700" data-id="element-9397">Filter by usage:</span>
          <select className="block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm" value={filterType} onChange={e => setFilterType(e.target.value)} data-id="element-9398">
            <option value="All" data-id="element-9399">All Associations</option>
            <option value="Vendor" data-id="element-9400">Vendors</option>
            <option value="Org Unit" data-id="element-9401">Org Units</option>
          </select>
        </div>
      </div>

      {/* Content */}
      <div className="mt-8 flex flex-col lg:flex-row gap-8" data-id="element-9402">
        {/* Contact List */}
        <div className={`flex-1 ${selectedContact ? 'hidden lg:block lg:w-2/3' : 'w-full'}`} data-id="element-9403">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg" data-id="element-9404">
            <table className="min-w-full divide-y divide-gray-300" data-id="element-9405">
              <thead className="bg-gray-50" data-id="element-9406">
                <tr data-id="element-9407">
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6" data-id="element-9408">
                    Name
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900" data-id="element-9409">
                    Role
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900" data-id="element-9410">
                    Contact Info
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900" data-id="element-9411">
                    Used In
                  </th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6" data-id="element-9412">
                    <span className="sr-only" data-id="element-9413">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-9414">
                {paginatedContacts.length > 0 ? paginatedContacts.map(contact => <tr key={contact.id} className={`hover:bg-gray-50 cursor-pointer ${selectedContact?.id === contact.id ? 'bg-indigo-50' : ''}`} onClick={e => handleViewContact(contact, e)} data-id="element-9415">
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6" data-id="element-9416">
                        <div className="flex items-center" data-id="element-9417">
                          <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold mr-3" data-id="element-9418">
                            {contact.name.charAt(0)}
                          </div>
                          <div data-id="element-9419">
                            {contact.name}
                            {contact.isPrimary && <span className="ml-2 inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800" data-id="element-9420">
                                Primary
                              </span>}
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500" data-id="element-9421">
                        {contact.role}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500" data-id="element-9422">
                        <div className="flex flex-col" data-id="element-9423">
                          <span className="flex items-center mb-1" data-id="element-9424">
                            <MailIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-9425" />
                            {getPrimaryEmail(contact)}
                          </span>
                          {getPrimaryPhone(contact) && <span className="flex items-center" data-id="element-9426">
                              <PhoneIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-9427" />
                              {getPrimaryPhone(contact)}
                            </span>}
                        </div>
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-500" data-id="element-9428">
                        <div className="flex flex-wrap gap-1" data-id="element-9429">
                          {contact.association ? <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getAssociationColor(contact.association.type)}`} data-id="element-9430">
                              {getAssociationIcon(contact.association.type)}
                              {contact.association.name}
                            </span> : <span className="text-gray-400 italic" data-id="element-9431">Unused</span>}
                        </div>
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6" data-id="element-9432">
                        <div className="flex justify-end gap-2" data-id="element-9433">
                          <button type="button" onClick={e => handleEditContact(contact, e)} className="text-indigo-600 hover:text-indigo-900" data-id="element-9434">
                            <EditIcon className="h-4 w-4" data-id="element-9435" />
                            <span className="sr-only" data-id="element-9436">
                              Edit, {contact.name}
                            </span>
                          </button>
                          <button type="button" onClick={e => handleDeleteClick(contact, e)} className="text-red-600 hover:text-red-900" data-id="element-9437">
                            <TrashIcon className="h-4 w-4" data-id="element-9438" />
                            <span className="sr-only" data-id="element-9439">
                              Delete, {contact.name}
                            </span>
                          </button>
                        </div>
                      </td>
                    </tr>) : <tr data-id="element-9440">
                    <td colSpan={5} className="py-8 text-center text-sm text-gray-500" data-id="element-9441">
                      No contacts found matching your criteria.
                    </td>
                  </tr>}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6" data-id="element-9442">
                <div className="flex-1 flex justify-between sm:hidden" data-id="element-9443">
                  <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-9444">
                    Previous
                  </button>
                  <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-9445">
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-9446">
                  <div data-id="element-9447">
                    <p className="text-sm text-gray-700" data-id="element-9448">
                      Showing{' '}
                      <span className="font-medium" data-id="element-9449">{startIndex + 1}</span> to{' '}
                      <span className="font-medium" data-id="element-9450">
                        {Math.min(endIndex, totalItems)}
                      </span>{' '}
                      of <span className="font-medium" data-id="element-9451">{totalItems}</span>{' '}
                      results
                    </p>
                  </div>
                  <div data-id="element-9452">
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-9453">
                      <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-9454">
                        <span className="sr-only" data-id="element-9455">Previous</span>
                        <ChevronLeftIcon className="h-5 w-5" data-id="element-9456" />
                      </button>
                      {[...Array(totalPages)].map((_, idx) => {
                    const page = idx + 1;
                    if (page === 1 || page === totalPages || page >= currentPage - 1 && page <= currentPage + 1) {
                      return <button key={page} onClick={() => handlePageChange(page)} className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${page === currentPage ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`} data-id="element-9457">
                              {page}
                            </button>;
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return <span key={page} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700" data-id="element-9458">
                              ...
                            </span>;
                    }
                    return null;
                  })}
                      <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-9459">
                        <span className="sr-only" data-id="element-9460">Next</span>
                        <ChevronRightIcon className="h-5 w-5" data-id="element-9461" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>}
          </div>
        </div>

        {/* Contact Details Panel */}
        {selectedContact && <div className="w-full lg:w-1/3 bg-white shadow rounded-lg border border-gray-200 p-6 h-fit sticky top-6" data-id="element-9462">
            <div className="flex justify-between items-start mb-6" data-id="element-9463">
              <div className="flex items-center" data-id="element-9464">
                <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xl mr-4" data-id="element-9465">
                  {selectedContact.name.charAt(0)}
                </div>
                <div data-id="element-9466">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-9467">
                    {selectedContact.name}
                  </h2>
                  <p className="text-sm text-gray-500" data-id="element-9468">
                    {selectedContact.role}
                  </p>
                </div>
              </div>
              <button onClick={() => setSelectedContact(null)} className="text-gray-400 hover:text-gray-500" data-id="element-9469">
                <XIcon className="h-5 w-5" data-id="element-9470" />
              </button>
            </div>

            <div className="space-y-6" data-id="element-9471">
              {/* Primary Status */}
              <div className="flex items-center text-sm" data-id="element-9472">
                <CheckIcon className={`h-5 w-5 mr-3 ${selectedContact.isPrimary ? 'text-green-500' : 'text-gray-300'}`} data-id="element-9473" />
                <span className={selectedContact.isPrimary ? 'text-gray-900' : 'text-gray-500'} data-id="element-9474">
                  {selectedContact.isPrimary ? 'Primary Contact' : 'Secondary Contact'}
                </span>
              </div>

              {/* Emails */}
              <div data-id="element-9475">
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3" data-id="element-9476">
                  Email Addresses
                </h3>
                <div className="space-y-3" data-id="element-9477">
                  {selectedContact.emails?.map((email: any) => <div key={email.id} className="flex items-start text-sm" data-id="element-9478">
                      <MailIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" data-id="element-9479" />
                      <div data-id="element-9480">
                        <a href={`mailto:${email.value}`} className="text-indigo-600 hover:text-indigo-900 block" data-id="element-9481">
                          {email.value}
                        </a>
                        <div className="flex items-center gap-2 mt-0.5" data-id="element-9482">
                          <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-9483">
                            {email.type}
                          </span>
                          {email.isPrimary && <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded" data-id="element-9484">
                              Primary
                            </span>}
                        </div>
                      </div>
                    </div>) || <div className="flex items-center text-sm" data-id="element-9485">
                      <MailIcon className="h-5 w-5 text-gray-400 mr-3" data-id="element-9486" />
                      <a href={`mailto:${selectedContact.email}`} className="text-indigo-600 hover:text-indigo-900" data-id="element-9487">
                        {selectedContact.email}
                      </a>
                    </div>}
                </div>
              </div>

              {/* Phones */}
              {(selectedContact.phones?.length > 0 || selectedContact.phone) && <div data-id="element-9488">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3" data-id="element-9489">
                    Phone Numbers
                  </h3>
                  <div className="space-y-3" data-id="element-9490">
                    {selectedContact.phones?.map((phone: any) => <div key={phone.id} className="flex items-start text-sm" data-id="element-9491">
                        <PhoneIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" data-id="element-9492" />
                        <div data-id="element-9493">
                          <a href={`tel:${phone.value}`} className="text-gray-900 hover:text-gray-700 block" data-id="element-9494">
                            {phone.value}
                          </a>
                          <div className="flex items-center gap-2 mt-0.5" data-id="element-9495">
                            <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-9496">
                              {phone.type}
                            </span>
                            {phone.isPrimary && <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded" data-id="element-9497">
                                Primary
                              </span>}
                          </div>
                        </div>
                      </div>) || <div className="flex items-center text-sm" data-id="element-9498">
                        <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" data-id="element-9499" />
                        <a href={`tel:${selectedContact.phone}`} className="text-gray-900 hover:text-gray-700" data-id="element-9500">
                          {selectedContact.phone}
                        </a>
                      </div>}
                  </div>
                </div>}

              {/* Addresses */}
              {selectedContact.addresses?.length > 0 && <div data-id="element-9501">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3" data-id="element-9502">
                    Addresses
                  </h3>
                  <div className="space-y-4" data-id="element-9503">
                    {selectedContact.addresses.map((addr: any) => <div key={addr.id} className="flex items-start text-sm" data-id="element-9504">
                        <MapPinIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" data-id="element-9505" />
                        <div data-id="element-9506">
                          <p className="text-gray-900" data-id="element-9507">{addr.street}</p>
                          <p className="text-gray-900" data-id="element-9508">
                            {addr.city}, {addr.state} {addr.postalCode}
                          </p>
                          <p className="text-gray-900" data-id="element-9509">{addr.country}</p>
                          <div className="flex items-center gap-2 mt-1" data-id="element-9510">
                            <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-9511">
                              {addr.type}
                            </span>
                            {addr.isPrimary && <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded" data-id="element-9512">
                                Primary
                              </span>}
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>}

              <div className="border-t border-gray-200 pt-6" data-id="element-9513">
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3" data-id="element-9514">
                  Used In
                </h3>
                {selectedContact.association ? <div className="py-3 flex items-center justify-between" data-id="element-9515">
                    <div className="flex items-center" data-id="element-9516">
                      <span className={`inline-flex items-center justify-center h-8 w-8 rounded-full mr-3 ${getAssociationColor(selectedContact.association.type)} bg-opacity-20`} data-id="element-9517">
                        {getAssociationIcon(selectedContact.association.type)}
                      </span>
                      <div data-id="element-9518">
                        <p className="text-sm font-medium text-gray-900" data-id="element-9519">
                          {selectedContact.association.name}
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-9520">
                          {selectedContact.association.type}
                        </p>
                      </div>
                    </div>
                    <button className="text-xs text-indigo-600 hover:text-indigo-900 font-medium" data-id="element-9521">
                      View
                    </button>
                  </div> : <p className="text-sm text-gray-500 italic" data-id="element-9522">
                    This contact is not currently associated with any record.
                  </p>}
              </div>

              <div className="border-t border-gray-200 pt-6 flex justify-end gap-3" data-id="element-9523">
                <button onClick={e => handleEditContact(selectedContact, e)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9524">
                  <EditIcon className="h-4 w-4 mr-2" data-id="element-9525" />
                  Edit
                </button>
                <button onClick={e => handleDeleteClick(selectedContact, e)} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-9526">
                  <TrashIcon className="h-4 w-4 mr-2" data-id="element-9527" />
                  Delete
                </button>
              </div>
            </div>
          </div>}
      </div>

      {/* Contact Form Modal */}
      {showContactForm && <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true" data-id="element-9528">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-9529">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowContactForm(false)} data-id="element-9530"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-9531">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-9532">
              <ContactForm contact={editingContact} onSave={handleSaveContact} onCancel={() => setShowContactForm(false)} locale={locale} data-id="element-9533" />
            </div>
          </div>
        </div>}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true" data-id="element-9534">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-9535">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowDeleteConfirm(null)} data-id="element-9536"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-9537">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6" data-id="element-9538">
              <div className="sm:flex sm:items-start" data-id="element-9539">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-9540">
                  <AlertTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" data-id="element-9541" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-9542">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title" data-id="element-9543">
                    Delete Contact
                  </h3>
                  <div className="mt-2" data-id="element-9544">
                    <p className="text-sm text-gray-500" data-id="element-9545">
                      Are you sure you want to delete {showDeleteConfirm.name}?
                      {showDeleteConfirm.association && <span className="block mt-2 font-medium text-red-600" data-id="element-9546">
                          Warning: This contact is currently associated with{' '}
                          {showDeleteConfirm.association.name}. Deleting it may
                          affect this record.
                        </span>}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse" data-id="element-9547">
                <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm" onClick={confirmDelete} data-id="element-9548">
                  Delete
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm" onClick={() => setShowDeleteConfirm(null)} data-id="element-9549">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}